import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import departmentService from '../services/departmentService';
import './DepartmentDetail.css';

const TYPES = [
  { value: 'fire', label: 'Fire' },
  { value: 'police', label: 'Police' },
  { value: 'ems', label: 'EMS' },
  { value: 'sheriff', label: 'Sheriff' },
  { value: 'search_and_rescue', label: 'Search & Rescue' },
  { value: 'other', label: 'Other' },
];

function DepartmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { addToast } = useToast();

  const [department, setDepartment] = useState(null);
  const [responders, setResponders] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isChief = department && department.chief_user_id === user?.id;
  const canManage = isChief || isAdmin;

  const load = async () => {
    setLoading(true);
    try {
      const [deptData, respData] = await Promise.all([
        departmentService.getDepartment(id),
        canManage ? departmentService.listResponders(id) : Promise.resolve({ responders: [] }),
      ]);
      setDepartment(deptData);
      setForm(deptData);
      setResponders(respData.responders || []);
    } catch (err) {
      addToast(err.message || 'Failed to load department', 'error');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canManage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = await departmentService.updateDepartment(id, form);
      setDepartment(data);
      setEditing(false);
      addToast('Department updated', 'success');
    } catch (err) {
      addToast(err.message || 'Failed to update department', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRotateCode = async () => {
    if (!window.confirm('This will invalidate the old join code. Continue?')) return;
    try {
      const data = await departmentService.rotateJoinCode(id);
      setDepartment(data);
      addToast('Join code rotated', 'success');
    } catch (err) {
      addToast(err.message || 'Failed to rotate join code', 'error');
    }
  };

  const handleRemoveResponder = async (responderId) => {
    if (!window.confirm('Remove this responder from the department?')) return;
    try {
      await departmentService.removeResponder(id, responderId);
      setResponders((prev) => prev.filter((r) => r.id !== responderId));
      addToast('Responder removed', 'success');
    } catch (err) {
      addToast(err.message || 'Failed to remove responder', 'error');
    }
  };

  if (loading) return <div className="department-detail">Loading...</div>;
  if (!department) return null;

  const address = [department.street, department.city, department.state, department.zip, department.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="department-detail">
      <h1>{department.name}</h1>
      <p className="subtitle">{TYPES.find((t) => t.value === department.type)?.label || department.type}</p>

      <div className="info-card">
        <p><strong>Phone:</strong> {department.phone || '(none)'}</p>
        <p><strong>Address:</strong> {address || '(none)'}</p>
        {canManage && (
          <div className="code-row">
            <strong>Join code:</strong> <code>{department.join_code}</code>
            <button type="button" className="secondary-button" onClick={handleRotateCode}>
              Rotate Code
            </button>
          </div>
        )}
      </div>

      {canManage && (
        <div className="form-card">
          <h2>{editing ? 'Edit Department' : 'Department Details'}</h2>
          {editing ? (
            <form onSubmit={handleSave} className="edit-form">
              <label>
                Name
                <input type="text" name="name" value={form.name || ''} onChange={handleChange} required />
              </label>
              <div className="row">
                <label>
                  Type
                  <select name="type" value={form.type || ''} onChange={handleChange}>
                    {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </label>
                <label>
                  Color
                  <input type="text" name="color" value={form.color || ''} onChange={handleChange} />
                </label>
              </div>
              <label>
                Phone
                <input type="text" name="phone" value={form.phone || ''} onChange={handleChange} />
              </label>
              <label>
                Street
                <input type="text" name="street" value={form.street || ''} onChange={handleChange} />
              </label>
              <div className="row">
                <label>
                  City
                  <input type="text" name="city" value={form.city || ''} onChange={handleChange} />
                </label>
                <label>
                  State
                  <input type="text" name="state" value={form.state || ''} onChange={handleChange} />
                </label>
              </div>
              <div className="row">
                <label>
                  ZIP
                  <input type="text" name="zip" value={form.zip || ''} onChange={handleChange} />
                </label>
                <label>
                  Country
                  <input type="text" name="country" value={form.country || ''} onChange={handleChange} />
                </label>
              </div>
              <label>
                Logo URL
                <input type="url" name="logo_url" value={form.logo_url || ''} onChange={handleChange} />
              </label>
              <div className="button-row">
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" className="secondary-button" onClick={() => { setForm(department); setEditing(false); }}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="button-row">
              <button type="button" className="primary-button" onClick={() => setEditing(true)}>
                Edit Department
              </button>
            </div>
          )}
        </div>
      )}

      {canManage && (
        <div className="responders-card">
          <h2>Responders</h2>
          {responders.length === 0 ? (
            <p className="empty">No responders have joined yet.</p>
          ) : (
            <ul className="responders-list">
              {responders.map((r) => (
                <li key={r.id}>
                  <div>
                    <strong>{r.name || '(no name)'}</strong>
                    <br />
                    <small>{r.email}</small>
                  </div>
                  <button type="button" className="danger-button" onClick={() => handleRemoveResponder(r.id)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default DepartmentDetail;
